import { useEffect, useState } from 'react';
import { get } from 'lodash';
import {
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Typography,
  FormControlLabel,
  Chip,
} from '@mui/material';
import { ExpandMore, Save } from '@mui/icons-material';
import Page from '../../components/Page';
import { getAllOmrPoints, getOmrPointDetail, updateOmrPoint } from '../../axiosLib/services/omrPoints/admin';

// Fields to skip when rendering coordinate sections (rendered separately)
const SKIP_FIELDS = [
  '_id', '__v', 'active', 'version', 'institutionId', 'createdAt', 'updatedAt',
  'FILL_THRESHOLD_50_MARKS', 'FILL_THRESHOLD_100_MARKS',
  'FILL_THRESHOLD_100_MARKS_ROLL', 'FILL_THRESHOLD_100_BYPASS',
  'FILL_THRESHOLD_100_MCQ_MEDICAL_ADMISSION_TYPE',
  'FILL_THRESHOLD_100_ROLL_MEDICAL_ADMISSION_TYPE',
];

// Threshold fields — rendered as numeric inputs
const THRESHOLD_FIELDS = [
  { key: 'FILL_THRESHOLD_50_MARKS', label: 'Fill Threshold - 50 Marks' },
  { key: 'FILL_THRESHOLD_100_MARKS', label: 'Fill Threshold - 100 Marks' },
  { key: 'FILL_THRESHOLD_100_MARKS_ROLL', label: 'Fill Threshold - 100 Marks Roll' },
  { key: 'FILL_THRESHOLD_100_BYPASS', label: 'Fill Threshold - 100 Bypass' },
  { key: 'FILL_THRESHOLD_100_MCQ_MEDICAL_ADMISSION_TYPE', label: 'Fill Threshold - 100 MCQ Medical Admission' },
  { key: 'FILL_THRESHOLD_100_ROLL_MEDICAL_ADMISSION_TYPE', label: 'Fill Threshold - 100 Roll Medical Admission' },
];

export default function OmrPointsEditor() {
  const [entries, setEntries] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [expandedPanels, setExpandedPanels] = useState({});
  // Holds the raw JSON text per section for editing
  const [jsonTexts, setJsonTexts] = useState({});
  // Tracks JSON parse errors per section
  const [jsonErrors, setJsonErrors] = useState({});

  useEffect(() => {
    loadEntries();
  }, []);

  const loadEntries = () => {
    getAllOmrPoints((err, result) => {
      if (!err) {
        setEntries(get(result, 'entries', []));
      }
    });
  };

  const loadDetail = (id) => {
    setLoading(true);
    getOmrPointDetail(id, (err, result) => {
      setLoading(false);
      if (!err) {
        const entry = get(result, 'entry', null);
        setData(entry);
        if (entry) {
          // Initialize JSON text for each array field
          const texts = {};
          Object.keys(entry).forEach((key) => {
            if (!SKIP_FIELDS.includes(key) && Array.isArray(entry[key])) {
              texts[key] = JSON.stringify(entry[key], null, 2);
            }
          });
          setJsonTexts(texts);
          setJsonErrors({});
        }
      } else {
        setSnackbar({ open: true, message: 'Failed to load entry', severity: 'error' });
      }
    });
  };

  const handleSelectEntry = (e) => {
    const id = e.target.value;
    setSelectedId(id);
    if (id) {
      loadDetail(id);
    } else {
      setData(null);
      setJsonTexts({});
      setJsonErrors({});
    }
  };

  const handleTopFieldChange = (field, value) => {
    setData({ ...data, [field]: value });
  };

  const handleJsonTextChange = (fieldName, text) => {
    setJsonTexts({ ...jsonTexts, [fieldName]: text });

    // Validate JSON on the fly
    try {
      const parsed = JSON.parse(text);
      if (!Array.isArray(parsed)) {
        setJsonErrors({ ...jsonErrors, [fieldName]: 'Must be a JSON array' });
      } else {
        // Clear error and update data
        const newErrors = { ...jsonErrors };
        delete newErrors[fieldName];
        setJsonErrors(newErrors);
        setData({ ...data, [fieldName]: parsed });
      }
    } catch (e) {
      setJsonErrors({ ...jsonErrors, [fieldName]: e.message });
    }
  };

  const formatJson = (fieldName) => {
    try {
      const parsed = JSON.parse(jsonTexts[fieldName]);
      setJsonTexts({ ...jsonTexts, [fieldName]: JSON.stringify(parsed, null, 2) });
      const newErrors = { ...jsonErrors };
      delete newErrors[fieldName];
      setJsonErrors(newErrors);
    } catch (e) {
      setJsonErrors({ ...jsonErrors, [fieldName]: e.message });
    }
  };

  const handleSave = () => {
    if (!selectedId || !data) return;

    // Check for any JSON errors
    const errorFields = Object.keys(jsonErrors);
    if (errorFields.length > 0) {
      setSnackbar({
        open: true,
        message: 'Fix JSON errors in: ' + errorFields.join(', '),
        severity: 'error',
      });
      return;
    }

    setSaving(true);

    // Build clean data from current jsonTexts
    const cleanData = { ...data };
    Object.keys(jsonTexts).forEach((key) => {
      try {
        cleanData[key] = JSON.parse(jsonTexts[key]);
      } catch (e) {
        // skip
      }
    });
    delete cleanData._id;
    delete cleanData.__v;

    updateOmrPoint(selectedId, cleanData, (err, result) => {
      setSaving(false);
      if (!err) {
        setSnackbar({ open: true, message: 'Saved successfully!', severity: 'success' });
        const entry = get(result, 'entry', data);
        setData(entry);
      } else {
        setSnackbar({ open: true, message: 'Save failed: ' + (err.message || 'Unknown error'), severity: 'error' });
      }
    });
  };

  const handleAccordionToggle = (panel) => {
    setExpandedPanels((prev) => ({ ...prev, [panel]: !prev[panel] }));
  };

  // Get all coordinate array field names from data
  const getCoordinateFieldNames = () => {
    if (!data) return [];
    return Object.keys(data).filter((key) => {
      if (SKIP_FIELDS.includes(key)) return false;
      return Array.isArray(data[key]);
    });
  };

  const coordFields = getCoordinateFieldNames();

  return (
    <Page title="OMR Points Editor">
      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
          <Typography variant="h4" gutterBottom>
            OMR Points Editor
          </Typography>
          {data && (
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
              onClick={handleSave}
              disabled={saving || Object.keys(jsonErrors).length > 0}
              sx={{
                background: 'linear-gradient(45deg, #00d4ff, #7b2ff7)',
                fontWeight: 'bold',
                px: 4,
                py: 1.5,
                '&:hover': { background: 'linear-gradient(45deg, #00b8d4, #6a1bd4)' },
              }}
            >
              {saving ? 'Saving...' : 'SAVE ALL CHANGES'}
            </Button>
          )}
        </Stack>

        {/* Entry selector */}
        <Card sx={{ mb: 3, p: 2 }}>
          <FormControl fullWidth>
            <InputLabel>Select OMR Points Entry</InputLabel>
            <Select value={selectedId} onChange={handleSelectEntry} label="Select OMR Points Entry">
              <MenuItem value="">
                <em>-- Select an entry --</em>
              </MenuItem>
              {entries.map((entry) => (
                <MenuItem key={entry._id} value={entry._id}>
                  {entry.version || entry._id} {entry.active ? '(Active)' : '(Inactive)'}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Card>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
            <CircularProgress />
          </Box>
        )}

        {data && !loading && (
          <>
            {/* Top-level fields */}
            <Card sx={{ mb: 3, p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                General Settings
              </Typography>
              <Stack spacing={2}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                  <TextField
                    label="Version"
                    value={get(data, 'version', '')}
                    onChange={(e) => handleTopFieldChange('version', e.target.value)}
                    fullWidth
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={get(data, 'active', false)}
                        onChange={(e) => handleTopFieldChange('active', e.target.checked)}
                        color="success"
                      />
                    }
                    label={data.active ? 'Active' : 'Inactive'}
                    sx={{ minWidth: 120 }}
                  />
                </Stack>
                <TextField
                  label="Institution ID"
                  value={
                    typeof get(data, 'institutionId') === 'object'
                      ? get(data, 'institutionId.$oid', get(data, 'institutionId._id', JSON.stringify(data.institutionId)))
                      : get(data, 'institutionId', '')
                  }
                  disabled
                  fullWidth
                  size="small"
                />
              </Stack>

              {/* Threshold settings */}
              <Typography variant="subtitle1" sx={{ mt: 3, mb: 1, fontWeight: 'bold', color: 'text.secondary' }}>
                Fill Thresholds
              </Typography>
              <Stack spacing={2}>
                {THRESHOLD_FIELDS.map(({ key, label }) => (
                  <TextField
                    key={key}
                    label={label}
                    type="number"
                    value={get(data, key, '')}
                    onChange={(e) => handleTopFieldChange(key, e.target.value === '' ? '' : Number(e.target.value))}
                    fullWidth
                    size="small"
                    InputProps={{ inputProps: { min: 0 } }}
                  />
                ))}
              </Stack>
            </Card>

            {/* Summary chips */}
            <Card sx={{ mb: 3, p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                Coordinate Sections ({coordFields.length} total)
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {coordFields.map((field) => (
                  <Chip
                    key={field}
                    label={`${field} (${(data[field] || []).length})`}
                    color={jsonErrors[field] ? 'error' : 'primary'}
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      handleAccordionToggle(field);
                      const el = document.getElementById('section-' + field);
                      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}
                  />
                ))}
              </Stack>
            </Card>

            {/* Coordinate array sections — JSON textarea */}
            {coordFields.map((fieldName) => (
              <Accordion
                key={fieldName}
                id={'section-' + fieldName}
                expanded={!!expandedPanels[fieldName]}
                onChange={() => handleAccordionToggle(fieldName)}
                sx={{
                  mb: 1,
                  '&:before': { display: 'none' },
                  borderRadius: '8px !important',
                  overflow: 'hidden',
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  sx={{
                    background: 'linear-gradient(90deg, #1a1a2e 0%, #16213e 100%)',
                    color: '#00d4ff',
                    '& .MuiAccordionSummary-expandIconWrapper': { color: '#00d4ff' },
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      {fieldName}
                    </Typography>
                    <Chip label={`${(data[fieldName] || []).length} rows`} size="small" color="info" />
                    {jsonErrors[fieldName] && (
                      <Chip label="JSON Error" size="small" color="error" />
                    )}
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 2 }}>
                  <TextField
                    multiline
                    fullWidth
                    minRows={8}
                    maxRows={30}
                    value={jsonTexts[fieldName] || '[]'}
                    onChange={(e) => handleJsonTextChange(fieldName, e.target.value)}
                    error={!!jsonErrors[fieldName]}
                    helperText={jsonErrors[fieldName] || ''}
                    sx={{
                      '& .MuiInputBase-input': {
                        fontFamily: 'monospace',
                        fontSize: '0.8rem',
                        lineHeight: 1.5,
                      },
                    }}
                  />
                  <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => formatJson(fieldName)}
                    >
                      Format JSON
                    </Button>
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}

            {/* Bottom save button */}
            <Box sx={{ mt: 3, mb: 5, display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
                onClick={handleSave}
                disabled={saving || Object.keys(jsonErrors).length > 0}
                size="large"
                sx={{
                  background: 'linear-gradient(45deg, #00d4ff, #7b2ff7)',
                  fontWeight: 'bold',
                  px: 6,
                  py: 2,
                  fontSize: '1rem',
                  '&:hover': { background: 'linear-gradient(45deg, #00b8d4, #6a1bd4)' },
                }}
              >
                {saving ? 'Saving...' : 'SAVE ALL CHANGES'}
              </Button>
            </Box>
          </>
        )}

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Page>
  );
}
